<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <p :class="$style.modalCardTitle">
        {{ modalTitle }}
      </p>
      <button @click="closeModal" type="button" :class="$style.headerButton">
        <i class="mdi mdi-close"></i>
      </button>
    </header>
    <section class="modal-card-body" :class="$style.modalCardBody">
      <template v-if="storedAudits.length === 0">
        <div class="modal-card-body padding-bottom82">
          <p class="has-text-centered">
            No audits are currently unavailable for this criterion.
          </p>
        </div>
      </template>
      <template v-else>
        <div
          v-for="audit in storedAudits"
          :class="$style.unavailableAuditsContainer"
          :key="audit.id"
        >
          <div :class="$style.unavailableAudits">
            <p>{{ audit.name }}</p>
            <p>Data collection {{ getAuditStatus(audit) }}</p>
          </div>
        </div>
      </template>
      <BasePagination
        :page.sync="page"
        :totalNumberOfPage="totalNumberOfPage"
        :perPage.sync="perPage"
        :totalCount="getTotalCount"
        :isFirstPage="isFirstPage"
        :isLastPage="isLastPage"
        :startItemIndex="startItemIndex"
        :endItemIndex="endItemIndex"
        :showPerPageOptions="false"
        v-if="getTotalCount > 10"
      />
    </section>
    <footer class="modal-card-foot">
      <button
        class="button is-primary is-rounded btn-site-properties"
        @click="closeModal"
      >
        OK
      </button>
    </footer>
  </div>
</template>

<script lang="ts">
import {
  GetFilteredAuditsByCriteriaPayload,
  AuditAvailabilityForCriteriaUpdate,
} from '@/store/modules/projects/types/projects.types';
import { AuditDetail } from '@/store/modules/audits/types/audits.types';
import { RootState } from '@/store/store';
import { ApiState, Pagination } from '@/store/types/general.types';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import BasePagination from '@/components/base/BasePagination.vue';

@Component({
  components: {
    BasePagination,
  },
})
export default class UnavailableCyclesDialogue extends Vue {
  @Prop()
  public modalTitle!: string;
  @Prop()
  public confirmButtonText!: string;
  @Prop()
  public projectId!: number;
  @Prop()
  public criterionId!: number;

  @Action('projects/getUnavailableFilteredAudits')
  public getUnavailableFilteredAudits!: (
    payload: GetFilteredAuditsByCriteriaPayload,
  ) => void;

  @State((state: RootState) => state.projects.unavailableAudits)
  public unavailableAudits!: Pagination<AuditDetail>;

  @State(
    (state: RootState) => state.projects.apiState.getUnavailableFilteredAudits,
  )
  public unavailableAuditsApiState!: ApiState;

  public storedAudits: AuditDetail[] = [];

  public perPage: number = 10;
  public page: number = 1;

  public closeModal() {
    this.$emit('close');
  }

  public mounted() {
    this.fetchData();
  }

  public getAuditStatus(audit: AuditDetail): string {
    if (audit.endedAt !== null) {
      return 'ended';
    } else if (audit.startedAt !== null) {
      return 'commenced';
    } else if (audit.status === 'Archived') {
      return 'archived';
    }
    return 'not started';
  }

  @Watch('page')
  onPageChange() {
    this.fetchData();
  }

  public fetchData() {
    const params: GetFilteredAuditsByCriteriaPayload = {
      projectId: this.projectId,
      criterionId: this.criterionId,
      filter: AuditAvailabilityForCriteriaUpdate.UNAVAILABLE,
      limit: this.perPage,
      page: this.page,
    };
    this.getUnavailableFilteredAudits(params);
  }

  @Watch('unavailableAuditsApiState', { immediate: true })
  @isTruthy
  public onFilteredAuditsByCriteriaChange(state: ApiState) {
    if (state.success) {
      this.storedAudits = this.unavailableAudits.items;
    }
  }

  get totalNumberOfPage(): number {
    return Math.ceil(this.getTotalCount / this.perPage);
  }

  get startItemIndex(): number {
    return this.page * this.perPage - this.perPage + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.getTotalCount, this.page * this.perPage);
  }

  get isFirstPage(): boolean {
    return this.page === 1;
  }

  get isLastPage(): boolean {
    return this.page === this.totalNumberOfPage;
  }

  get getCurrentPage(): number {
    return this.page;
  }

  get getCurrentPerPage(): number {
    return this.perPage;
  }

  get getTotalCount(): number {
    if (!this.unavailableAudits) {
      return 0;
    }
    return this.unavailableAudits.totalItems != null
      ? this.unavailableAudits.totalItems
      : 0;
  }
}
</script>

<style module lang="scss">
.unavailableAuditsContainer {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.unavailableAudits {
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #dfe2e5;
  padding: 1rem 0;
}

.modalCardBody {
  padding: 0.5rem 2rem 2.5rem 2rem !important;
}

.customPaginationOverride nav li {
  padding-right: 0 !important;
}

.modalCardTitle {
  width: 90%;
  color: $primary;
  font-size: $size-3;
  font-weight: $weight-bold;
  line-height: 22px;
  text-align: left;
}

.headerButton {
  border: none;
  background: none;
  padding: 0;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: $dark;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease-in-out;
  margin-left: 1rem;

  i {
    font-size: $size-3;
    line-height: 1;
  }

  &:hover {
    background-color: $grey-light-5;
  }

  &:focus {
    outline: none;
  }
}
</style>
